// Message state management logic extracted from Emissary for testing

import type { Message } from '@/lib/firebase';
import type { ChatMessage as LocalChatMessage } from '@/types';

export interface MessageState {
  firestoreMessages: Message[];
  streamingMessage: LocalChatMessage | null;
  streamingThinkingContent: string;
  preservedThinkingContent: string;
  isStreaming: boolean;
  error: string | null;
  messagesLoaded: boolean; // Track if initial message load from Firestore is complete
}

export const initialMessageState: MessageState = {
  firestoreMessages: [],
  streamingMessage: null,
  streamingThinkingContent: '',
  preservedThinkingContent: '',
  isStreaming: false,
  error: null,
  messagesLoaded: false // Initially false until Firestore responds
};

export type MessageAction = 
  | { type: 'SET_FIRESTORE_MESSAGES'; messages: Message[] }
  | { type: 'START_STREAMING'; message: LocalChatMessage }
  | { type: 'UPDATE_STREAMING_CONTENT'; chunk: string }
  | { type: 'UPDATE_THINKING_CONTENT'; content: string }
  | { type: 'END_STREAMING'; finalContent: string; thinkingContent: string }
  | { type: 'STREAMING_ERROR'; error: string }
  | { type: 'CLEAR_STREAMING_MESSAGE' }
  | { type: 'PRESERVE_THINKING_CONTENT'; content: string }
  | { type: 'UPDATE_STREAMING_METADATA'; structuredOutput?: any };

export function messageReducer(state: MessageState, action: MessageAction): MessageState {
  switch (action.type) {
    case 'SET_FIRESTORE_MESSAGES':
      return {
        ...state,
        firestoreMessages: action.messages,
        messagesLoaded: true // Mark as loaded when we receive messages from Firestore
      };

    case 'START_STREAMING':
      return {
        ...state,
        isStreaming: true,
        streamingMessage: action.message,
        streamingThinkingContent: '',
        error: null
      };

    case 'UPDATE_STREAMING_CONTENT':
      if (!state.streamingMessage) return state;
      return {
        ...state,
        streamingMessage: {
          ...state.streamingMessage,
          content: state.streamingMessage.content + action.chunk
        }
      };

    case 'UPDATE_THINKING_CONTENT':
      return {
        ...state,
        streamingThinkingContent: action.content
      };

    case 'END_STREAMING':
      if (!state.streamingMessage) return state;
      return {
        ...state,
        isStreaming: false,
        streamingMessage: {
          ...state.streamingMessage,
          content: action.finalContent,
          thinkingContent: action.thinkingContent
        },
        preservedThinkingContent: action.thinkingContent
      };

    case 'STREAMING_ERROR':
      return {
        ...state,
        isStreaming: false,
        error: action.error,
        preservedThinkingContent: state.streamingThinkingContent
      };

    case 'CLEAR_STREAMING_MESSAGE':
      return {
        ...state,
        streamingMessage: null
      };

    case 'PRESERVE_THINKING_CONTENT':
      return {
        ...state,
        preservedThinkingContent: action.content
      };

    case 'UPDATE_STREAMING_METADATA':
      if (!state.streamingMessage) return state;
      return {
        ...state,
        streamingMessage: {
          ...state.streamingMessage,
          structuredOutput: action.structuredOutput
        }
      };

    default:
      return state;
  }
}

// Helper functions for common operations
export function shouldClearStreamingOnNewMessage(
  state: MessageState,
  timeDiffMs: number = 5000
): boolean {
  if (state.isStreaming || !state.streamingMessage) {
    return false;
  }

  const latestBotMessage = state.firestoreMessages
    .filter(msg => msg.sender === 'assistant')
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];

  if (!latestBotMessage) return false;

  const timeDiff = Date.now() - (latestBotMessage.timestamp || 0);
  return timeDiff < timeDiffMs;
}

export function getEffectiveThinkingContent(state: MessageState): string {
  return state.streamingThinkingContent || state.preservedThinkingContent;
}